import React, { useState, useEffect } from 'react';
import { bool } from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useStore } from '@thd-nucleus/experience-context';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { StoreLocatorMetadata } from '@thd-olt-component-react/metadata';
import { useNavigate } from '@thd-olt-component-react/router';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { StoreSearchFeatures } from '@thd-olt-component-react/store-features';
import {
  Map,
  Results,
  SearchInput,
  Services,
  StoreSearch
} from '@thd-olt-component-react/store-search';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { CustomerContext } from '@thd-olt-functional/customer-information';

import { useMobileBreakpoint } from '../../utils/stylesUtils';
import { getLocalizedStoreId, useGetCoordinates, useGetParams } from './utils';

export const StoreLocator = () => {
  declareContexts([CustomerContext], { freeze: true });

  const { storeId } = useStore({ varnish: false });
  const isMobile = useMobileBreakpoint();
  const theme = useTheme(StoreLocator);
  const coordinates = useGetCoordinates();
  const queryParams = useGetParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(
    queryParams.zipcode || queryParams.storeId || getLocalizedStoreId() || storeId);
  const [searchCoordinates, setSearchCoordinates] = useState(coordinates);

  const mapOptions = { height: isMobile ? '350px' : '645px' };
  const { hideStoreSearchFeatures, hideSponsoredCarousel, hideSponsoredBanner } = theme.props;

  const setSearchInputHandler = (value) => setSearchInput(value)

  const setSearchCoordinatesHandler = (coordinatesValue) => {
    const { lat, lng } = coordinatesValue;
    if (lat && lng) {
      navigate(`?lat=${lat}&lng=${lng}`);
    }
    setSearchCoordinates(coordinatesValue);
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.store-locator-page-load', {});
  }, []);

  return (
    <>
      { /* This should be one component in metadata */}
      <ErrorBoundary name="store-locator-metadata">
        <StoreLocatorMetadata />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-mx-auto">
        <div className="sui-pt-2 sui-px-2 2xl:sui-px-0">
          <Typography variant="h1">Store Locator</Typography>

          <ErrorBoundary name="store-locator-search-map">
            <div className="sui-mt-2">
              <StoreSearch
                searchInput={searchInput}
                setSearchInput={setSearchInputHandler}
                searchCoordinates={searchCoordinates}
                setSearchCoordinates={setSearchCoordinatesHandler}
              >
                <div
                  className="sui-grid sui-grid-12 lg:sui-grid-cols-4 sui-gap-4 sui-mb-3 lg:sui-mb-0"
                >
                  <div className="sui-col-span-12 lg:sui-col-span-1">
                    <SearchInput showStoreDirectoryLink />
                  </div>
                  <div className="sui-col-span-12 lg:sui-col-span-3">
                    <div className="lg:sui-mt-2">
                      <Services />
                    </div>
                  </div>
                </div>

                <div className="sui-grid sui-grid-12 lg:sui-grid-cols-4 sui-gap-4">
                  <div className="sui-col-span-12 lg:sui-col-span-1">
                    <Results />
                  </div>
                  <div className="sui-col-span-12 lg:sui-col-span-3">
                    <Map mapOptions={mapOptions} />
                  </div>
                </div>
              </StoreSearch>
            </div>
          </ErrorBoundary>

          {!hideStoreSearchFeatures && (
            <ErrorBoundary name="store-locator-search-features">
              <div className="sui-mt-8 sui-flex sui-flex-col sui-gap-2">
                <Typography variant="h2">Store Features</Typography>
                <StoreSearchFeatures />
              </div>
            </ErrorBoundary>
          )}

          {!hideSponsoredCarousel && (
            <ErrorBoundary name="store-locator-sponsored-carousel">
              <div className="sui-mt-8">
                <RetailMediaCarousel
                  plaLocation="sponsoredCarouselStorePage"
                  pageContext={{
                    schema: 'storepage_sponsored',
                    isStorePage: true
                  }}
                  mockEnabledFeatureSwitch
                  dynamic={{
                    pageType: 'storeLocator'
                  }}
                  hydrator={{
                    className: 'grid',
                    delay: 1500,
                    scrollBuffer: 300
                  }}
                />
              </div>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </>
  );
};

StoreLocator.displayName = 'StoreLocator';
StoreLocator.propTypes = {};

StoreLocator.themeProps = {
  hideStoreSearchFeatures: bool,
  hideSponsoredCarousel: bool,
  hideSponsoredBanner: bool,
};

StoreLocator.defaultThemeProps = {
  hideStoreSearchFeatures: false,
  hideSponsoredCarousel: false,
  hideSponsoredBanner: false,
};

StoreLocator.dataModel = extend({},
  StoreSearch
);
